import { PLATFORM_CURRENCIES, PLATFORM_LOCALES } from "@blockdaemon/widget-metadata";
import { z } from "zod";

/**
 * Logo configuration - supports either a single URL or separate light/dark logos
 */
function isValidLogoUrl(val: string): boolean {
  return URL.canParse(val) || val.startsWith("/");
}

const LogoConfigSchema = z.union([
  z.string().refine((val) => !val || val.trim() === "" || isValidLogoUrl(val), {
    message: "Must be a valid URL or relative path starting with /",
  }),
  z.object({
    /** Light-coloured logo variant — displayed on dark backgrounds (dark colour scheme). */
    lightLogo: z.string().refine(isValidLogoUrl, {
      message: "Must be a valid URL or relative path starting with /",
    }),
    /** Dark-coloured logo variant — displayed on light backgrounds (light colour scheme). */
    darkLogo: z.string().refine(isValidLogoUrl, {
      message: "Must be a valid URL or relative path starting with /",
    }),
  }),
]);

/**
 * Width configuration - supports either device types or custom pixel values
 */
const WidthSchema = z.union([
  z.enum(["mobile", "tablet", "desktop"]),
  z.number().min(375).max(1200),
]);

/**
 * Height configuration - supports either device types or custom pixel values
 */
const HeightSchema = z.union([
  z.enum(["mobile", "tablet", "desktop"]),
  z.number().min(480).max(1200),
]);

/**
 * ISO 4217 currency code validation (platform-wide; products may allow a subset).
 */
const ISO4217CurrencyCodeSchema = z.enum(PLATFORM_CURRENCIES, {
  message: "Must be one of: USD, KRW, JPY",
});

/**
 * Locale validation schema (platform-wide; products may allow a subset).
 */
const LocaleSchema = z.enum(PLATFORM_LOCALES, {
  message: "Must be one of: en-US, ko-KR, ja-JP",
});

/**
 * Zod schema for widget appearance configuration
 *
 * Note: The theme setting can be overridden by customers when embedding the widget
 * by adding a 'colorScheme' URL parameter (e.g., ?colorScheme=dark)
 * This allows customers to set the color scheme without modifying the server configuration.
 * Similarly, currency and locale can be overridden via the 'currency' and 'locale' URL parameters.
 */
export const WidgetAppearanceSchema = z.object({
  logoUrl: LogoConfigSchema.optional(),
  sizingMode: z.enum(["fixed", "responsive"]).default("fixed"),
  width: WidthSchema,
  height: HeightSchema.default("mobile"),
  theme: z.enum(["light", "dark"]).default("light"),
  builtByBlockdaemon: z.boolean().default(true),
  coreNavigation: z.boolean().default(true),
  externalLinks: z.boolean().default(true),
  locale: LocaleSchema,
  currency: ISO4217CurrencyCodeSchema,
});
